import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { apiFetch } from '../../services/apiClient';
import GlancePillows from './GlancePillows';
import ActivityPills from '../../components/ActivityPills';
import MorningBriefs from '../../components/MorningBriefs';
import ProjectsTable from './ProjectsTable';
import { CreatePoModal } from './CreatePoModal';

// "[PM] at a Glance" — the Executive dashboard layout (same ribbons top & bottom)
// scoped to a single project manager's jobs. Reached from the Exec dashboard.
const WF_META = {
  vertical_drilling:   { label: 'Vertical Drilling',   icon: '🕳️', unit: 'holes' },
  horizontal_drilling: { label: 'Directional Drilling', icon: '↔️', unit: 'bores' },
  loops:               { label: 'Loops',               icon: '🔁', unit: 'cards' },
  nve:                 { label: 'NVE',                  icon: '⚡', unit: 'meters' },
  usa_digs:            { label: 'USA Digs',             icon: '🚧', unit: 'tickets' },
  submittals:          { label: 'Submittals',          icon: '📋', unit: 'items' },
  permits:             { label: 'Permits',             icon: '📜', unit: 'permits' },
};
const wfMeta = t => WF_META[t] || { label: (t || 'Other').replace(/_/g, ' '), icon: '📁', unit: 'cards' };

const card = { background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12 };
const sectionLabel = { fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' };

function Metric({ label, value, danger, onClick }) {
  return (
    <div onClick={onClick}
      style={{ background: 'var(--surface-alt, #f8fafc)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, color: danger && value > 0 ? '#dc2626' : 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, fontWeight: 600 }}>{label}</div>
    </div>
  );
}


export default function PmGlance() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [favIds, setFavIds] = useState(() => new Set());
  const [usaByProject, setUsaByProject] = useState({});
  const [liveCounts, setLiveCounts] = useState({});
  const [vendors, setVendors] = useState([]);
  const [showPo, setShowPo] = useState(false);

  useEffect(() => {
    setData(null); setError(null);
    apiFetch(`/exec/pm/${id}/summary`).then(setData).catch(e => setError(e?.error || 'Failed to load this PM.'));
  }, [id]);
  // Same supporting data the Projects page uses, so the table matches.
  useEffect(() => {
    apiFetch('/project-favorites').then(ids => setFavIds(new Set(Array.isArray(ids) ? ids : []))).catch(() => {});
    apiFetch('/usa-status-by-project').then(m => setUsaByProject(m && typeof m === 'object' ? m : {})).catch(() => {});
    apiFetch('/projects/live-counts').then(m => setLiveCounts(m && typeof m === 'object' ? m : {})).catch(() => {});
    apiFetch('/vendors/active').then(v => setVendors(Array.isArray(v) ? v : [])).catch(() => {});
  }, []);

  async function toggleFavorite(e, pid) {
    e.stopPropagation();
    const isFav = favIds.has(pid);
    setFavIds(prev => { const n = new Set(prev); if (isFav) n.delete(pid); else n.add(pid); return n; });
    try { await apiFetch(`/project-favorites/${pid}`, { method: isFav ? 'DELETE' : 'POST' }); }
    catch { setFavIds(prev => { const n = new Set(prev); if (isFav) n.add(pid); else n.delete(pid); return n; }); }
  }

  const t = data?.totals || {};
  const workflows = data?.workflows || [];
  const shop = data?.shop || { new: 0, working: 0, complete: 0 };
  const projects = data?.projects || [];

  return (
    <AdminLayout>
      <div style={{ maxWidth: 1100 }}>
        <button onClick={() => navigate('/admin')}
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
          ‹ Executive Dashboard
        </button>

        {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>{error}</div>}
        {!data && !error && <div style={{ color: 'var(--text-muted)', padding: '30px 0' }}>Loading…</div>}

        {data && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#eef2ff', color: '#3730a3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15 }}>
                {data.pm.name.split(' ').map(x => x[0]).slice(0, 2).join('')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{data.pm.name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Project Manager · at a glance</div>
              </div>
            </div>

            {/* Activity feed + Scotto's summary — scoped to this PM's jobs. A Write PO
                pill sits at the far left of the row. */}
            <div style={{ marginBottom: 20 }}>
              <ActivityPills pageName={`${data.pm.name.split(' ')[0]}'s`} summaryLabel={`${data.pm.name.split(' ')[0]}'s Update`} scope="pm" scopeId={id} endpoint={`/exec/pm/${id}/activity`}
                leading={[{ icon: '🧾', label: 'Write PO', onClick: () => setShowPo(true) }]} />
            </div>
            {/* Stays open after Create so the green "PO Complete" card (number, job, ⧉ copy)
                can be read — it used to blink away the moment the PO was written (Ryan,
                Sep 15 and again Sep 29 2026). × / Done on the card close it. */}
            <CreatePoModal open={showPo} onClose={() => setShowPo(false)} projects={projects} vendors={vendors}
              onCreated={() => {}} />

            {/* Morning Brief — this PM's daily rundown + podcast, same visualization
                as the Executive dashboard, scoped to this PM. */}
            <h2 style={sectionLabel}>🎙️ Morning Brief</h2>
            <div style={{ marginBottom: 26 }}>
              <MorningBriefs audience="pm" pmId={id} pmName={data.pm.name} />
            </div>

            {/* Top ribbon — this PM's numbers (same expand-in-place pillows as the Exec) */}
            <GlancePillows totals={t} lists={data.lists} navigate={navigate} />

            {/* Work Flows ribbon — this PM's jobs */}
            <h2 style={sectionLabel}>Work Flows</h2>
            {workflows.length === 0 ? (
              <div style={{ ...card, borderStyle: 'dashed', padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, marginBottom: 30 }}>No workflow cards on this PM's active jobs.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 30 }}>
                {workflows.map(w => {
                  const m = wfMeta(w.type);
                  const pct = w.total ? Math.round((w.complete / w.total) * 100) : 0;
                  return (
                    <div key={w.type} onClick={() => navigate(`/admin/workflows/${w.type}`)}
                      style={{ ...card, padding: '13px 15px', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>{m.icon} {m.label}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{w.complete}/{w.total} {m.unit}</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: '#1e3a5f' }} />
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>{w.active} in progress · {pct}% complete</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Shop ribbon — this PM's jobs */}
            <h2 style={sectionLabel}>Shop</h2>
            <div onClick={() => navigate('/admin/shop')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 30, cursor: 'pointer' }}>
              <Metric label="New requests" value={shop.new || 0} />
              <Metric label="Working on" value={shop.working || 0} />
              <Metric label="Completed" value={shop.complete || 0} />
            </div>

            {/* Their projects — same layout as the Projects page (no PM column / delete) */}
            <h2 style={sectionLabel}>{data.pm.name.split(' ')[0]}'s Projects</h2>
            {projects.length === 0 ? (
              <div style={{ ...card, borderStyle: 'dashed', padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>No active jobs assigned to this PM.</div>
            ) : (
              <div className="admin-card" style={{ overflow: 'hidden' }}>
                <ProjectsTable
                  rows={projects}
                  onOpen={p => navigate(`/admin/new-projects/${p.id}`)}
                  favIds={favIds}
                  onToggleFavorite={toggleFavorite}
                  usaByProject={usaByProject}
                  liveCounts={liveCounts}
                  showPM={false}
                  canDelete={false}
                />
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
